import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";

export interface TenantInfo {
  id: string;
  slug: string;
  name: string;
  status: "ONBOARDING" | "ACTIVE" | "SUSPENDED";
  logoPath: string;
  runtimeLoaded: boolean;
  recentAlertsCount: number;
  _count: { memberships: number };
  createdAt: string;
  updatedAt: string;
}

export interface TenantRecentAlert {
  id: string;
  title: string;
  locations: string[];
  source: string;
  receivedAt: number;
}

export interface TenantOrefAlertLogRow {
  id: string;
  alertId: string;
  source: string;
  title: string;
  category: string;
  locations: string[];
  isMatch: boolean;
  dedupBlocked: boolean;
  receivedAt: string;
  alertDate: string;
}

export interface TenantMessageLogRow {
  id: string;
  timestamp: string;
  cardId: string | null;
  templateName: string | null;
  mode: string | null;
  sent: boolean;
  sentBy: string;
  orefTitle: string;
  failReason: string;
  triggerSource: string;
}

const tenantBase = (slug: string) => `/t/${slug}`;

export const useTenantInfo = (slug: string | undefined) =>
  useQuery({
    queryKey: ["tenant", slug, "info"] as const,
    queryFn: async () => (await axios.get<TenantInfo, AxiosResponse<TenantInfo>>(`${tenantBase(slug!)}/info`)).data,
    enabled: !!slug,
    refetchInterval: 15_000,
  });

export const useTenantOrefAlertLog = (slug: string | undefined) =>
  useQuery({
    queryKey: ["tenant", slug, "oref-alert-log"] as const,
    queryFn: async () =>
      (await axios.get<TenantOrefAlertLogRow[], AxiosResponse<TenantOrefAlertLogRow[]>>(
        `${tenantBase(slug!)}/oref-alert-log`,
      )).data,
    enabled: !!slug,
    refetchInterval: 10_000,
  });

export const useTenantMessageLog = (slug: string | undefined) =>
  useQuery({
    queryKey: ["tenant", slug, "message-log"] as const,
    queryFn: async () =>
      (await axios.get<TenantMessageLogRow[], AxiosResponse<TenantMessageLogRow[]>>(
        `${tenantBase(slug!)}/message-log`,
      )).data,
    enabled: !!slug,
    refetchInterval: 10_000,
  });
